import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  getAuthSession,
  GETSINGLEFULLCONFIGS,
  GETSINGLECONFIGS,
  KOTPRINTITEMWISE,
  KOTPRINTNOWISE,
  PERSISTCONFIGS,
  PRINTJARALLOWED,
  PRINTER_AGENT,
  SUCCESS,
  getPreUrlAccordingToCart
} from "../../constants/constant";
import { snpAxios } from "../global/api";
import SnpButton from "../global/SnpButton";
import Snackbar from "../ui/Snackbar";
import { listSilentWindowsPrinters, prepareSilentPrinter } from "./silentPrinting";
import "./PrinterConfig.css";

const BASE_CHANNELS = [
  { key: "kot", title: "KOT printer", description: "New kitchen order tickets sent from billing." },
  { key: "user", title: "Customer / user printer", description: "The bill copy given to the customer." },
  { key: "shop", title: "Shop printer", description: "The bill copy retained by the shop." },
  { key: "gen", title: "Generate Bill printer", description: "The bill produced by the Generate Bill action." },
];

const REPRINT_CHANNELS = [
  { key: "kot_previous", title: "Previous KOT printer", description: "Kitchen Order Tickets printed again by KOT number.", permission: KOTPRINTNOWISE },
  { key: "kot_item", title: "Item-wise KOT printer", description: "Kitchen Order Tickets and item audit-trail reprints.", permission: KOTPRINTITEMWISE },
];

const ALL_CHANNELS = [...BASE_CHANNELS, ...REPRINT_CHANNELS];
const NORMAL_PRINT = "normal_print";
const SILENT_DESKTOP = "SILENT_DESKTOP";
const SILENT_MOBILE_TABLET = "Silent_MOBILE_TABLET";
const MOBILE_APP_LINKS_FILE = `${process.env.PUBLIC_URL || ""}/printer-mobile-apps.json`;

const TYPES = [
  { value: "tcp", label: "TCP / Ethernet" },
  { value: "windows", label: "Windows printer" },
  { value: "usb", label: "USB / WebUSB" },
  { value: "ble", label: "Bluetooth Low Energy" },
  { value: "serial", label: "Bluetooth Classic / Serial" },
];

const PROFILES = [
  { value: "58mm-384", label: "58 mm / 384 dots", width: 58, dots: 384 },
  { value: "58mm-432", label: "58 mm / 432 dots", width: 58, dots: 432 },
  { value: "76mm-512", label: "76 mm / 512 dots", width: 76, dots: 512 },
  { value: "80mm-512", label: "80 mm / 512 dots", width: 80, dots: 512 },
  { value: "80mm-576", label: "80 mm / 576 dots", width: 80, dots: 576 },
  { value: "112mm-832", label: "112 mm / 832 dots", width: 112, dots: 832 },
];

const defaultMap = () => ALL_CHANNELS.reduce((map, { key }) => ({
  ...map,
  [`${key}_enabled`]: false,
  [`${key}_type`]: "tcp",
  [`${key}_profile`]: "80mm-576",
  [`${key}_paper_width_mm`]: 80,
  [`${key}_raster_width`]: 576,
  [`${key}_port`]: 9100,
  [`${key}_cut_mode`]: "partial",
}), { silent_agent_port: 17312 });

const persistedMap = (configMap) => ALL_CHANNELS.reduce((map, { key }) => {
  const enabled = configMap[`${key}_enabled`] === true;
  if (!enabled) return map;

  map[`${key}_enabled`] = true;
  Object.entries(configMap).forEach(([configKey, value]) => {
    if (configKey.startsWith(`${key}_`)) map[configKey] = value;
  });
  return map;
}, { silent_agent_port: configMap.silent_agent_port || 17312 });

const Field = ({ label, hint, children }) => (
  <label className="printer-field">
    <span>{label}</span>
    {children}
    {hint && <small>{hint}</small>}
  </label>
);

const GooglePlayIcon = () => (
  <svg className="printer-store-svg" viewBox="0 0 32 32" aria-hidden="true">
    <path fill="#4dd0e1" d="M3 3.8v24.4L17.2 16 3 3.8z" />
    <path fill="#ffd54f" d="m17.2 16 4.4-3.8 5.5 3.1c1.2.7 1.2 1.5 0 2.2l-5.5 3.1-4.4-4.6z" />
    <path fill="#ef5350" d="m3 3.8 18.6 8.4-4.4 3.8L3 3.8z" />
    <path fill="#66bb6a" d="m3 28.2 18.6-7.6-4.4-4.6L3 28.2z" />
  </svg>
);

const AppleStoreIcon = () => (
  <svg className="printer-store-svg" viewBox="0 0 32 32" aria-hidden="true">
    <path fill="currentColor" d="M22.8 17.1c0-3 2.5-4.5 2.6-4.6-1.4-2.1-3.7-2.4-4.5-2.4-1.9-.2-3.7 1.1-4.7 1.1-1 0-2.6-1.1-4.3-1-2.2 0-4.2 1.3-5.3 3.2-2.3 3.9-.6 9.7 1.6 12.9 1.1 1.5 2.3 3.2 4 3.1 1.6-.1 2.2-1 4.2-1s2.5 1 4.2 1c1.7 0 2.8-1.5 3.8-3 1.2-1.8 1.8-3.6 1.8-3.7-.1 0-3.4-1.3-3.4-5.6zM19.6 8c.9-1.1 1.5-2.7 1.4-4.3-1.4.1-3 .9-4 2-1 1.1-1.6 2.6-1.4 4.2 1.5.1 3-.8 4-1.9z" />
  </svg>
);

const MicrosoftStoreIcon = () => (
  <svg className="printer-store-svg" viewBox="0 0 32 32" aria-hidden="true">
    <path fill="#f25022" d="M3 4h12v12H3z" /><path fill="#7fba00" d="M17 4h12v12H17z" />
    <path fill="#00a4ef" d="M3 18h12v12H3z" /><path fill="#ffb900" d="M17 18h12v12H17z" />
  </svg>
);

export default function PrinterConfig({ embedded = false, onSaved } = {}) {
  const activeShop = useSelector((state) => state.setActiveShopHashReducer.activeShopHash);
  const apiUrl = useSelector((state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL);
  const [mode, setMode] = useState(NORMAL_PRINT);
  const [configMap, setConfigMap] = useState(defaultMap);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kotPermissions, setKotPermissions] = useState({ [KOTPRINTNOWISE]: false, [KOTPRINTITEMWISE]: false });
  const [license, setLicense] = useState(null);
  const [licenseBusy, setLicenseBusy] = useState(false);
  const [printerAgentUrl, setPrinterAgentUrl] = useState("");
  const [windowsPrinters, setWindowsPrinters] = useState([]);
  const [printerListBusy, setPrinterListBusy] = useState(false);
  const [mobileAppLinks, setMobileAppLinks] = useState(null);
  const [toast, setToast] = useState({ open: false, type: "success", text: "" });

  const baseUrl = useMemo(
    () => `${apiUrl.BACKEND_BASE_URL}${getPreUrlAccordingToCart(activeShop.cartType)}`,
    [apiUrl, activeShop]
  );
  const apiRoot = apiUrl.BACKEND_BASE_URL;
  const licenseUrl = `${apiRoot}printer-agent-license?token=${encodeURIComponent(getAuthSession().apiToken)}&shopHash=${encodeURIComponent(activeShop.id)}`;
  const showMessage = useCallback((type, text) => {
    setToast({ open: true, type, text });
  }, []);

  const visibleChannels = useMemo(() => [
    ...BASE_CHANNELS,
    ...REPRINT_CHANNELS.filter((channel) => kotPermissions[channel.permission]),
  ], [kotPermissions]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const url = `${baseUrl}${GETSINGLEFULLCONFIGS}?adminId=${encodeURIComponent(getAuthSession().apiToken)}&configName=${encodeURIComponent(PRINTJARALLOWED)}&shopHash=${encodeURIComponent(activeShop.id)}`;
    axios.get(url).then(({ data }) => {
      if (!mounted) return;
      const savedMap = data?.jcJson?.map || {};
      setRecord(data?.jcJson || null);
      const savedMode = String(data?.jcJson?.val || "");
      setMode(savedMode.toUpperCase() === SILENT_DESKTOP
        ? SILENT_DESKTOP
        : savedMode.toLowerCase() === SILENT_MOBILE_TABLET.toLowerCase() ? SILENT_MOBILE_TABLET : NORMAL_PRINT);
      setConfigMap({ ...defaultMap(), ...savedMap });
    }).catch(() => {
      if (mounted) showMessage("error", "Printer configuration could not be loaded.");
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [activeShop.id, baseUrl, showMessage]);

  useEffect(() => {
    let mounted = true;
    fetch(MOBILE_APP_LINKS_FILE, { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!mounted || !data) return;
        setMobileAppLinks({
          androidUrl: String(data.androidUrl || "").trim(),
          iosUrl: String(data.iosUrl || "").trim(),
        });
      })
      .catch(() => mounted && setMobileAppLinks(null));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFlag = async (configName) => {
      const url = `${baseUrl}${GETSINGLECONFIGS}?adminId=${encodeURIComponent(getAuthSession().apiToken)}&configName=${encodeURIComponent(configName)}&shopHash=${encodeURIComponent(activeShop.id)}`;
      try {
        const { data } = await axios.get(url);
        return String(data).toLowerCase() === "yes";
      } catch (_) {
        return false;
      }
    };
    Promise.all([loadFlag(KOTPRINTNOWISE), loadFlag(KOTPRINTITEMWISE)]).then(([previousKot, itemKot]) => {
      if (mounted) setKotPermissions({ [KOTPRINTNOWISE]: previousKot, [KOTPRINTITEMWISE]: itemKot });
    });
    const agentConfigUrl = `${baseUrl}${GETSINGLECONFIGS}?adminId=${encodeURIComponent(getAuthSession().apiToken)}&configName=${encodeURIComponent(PRINTER_AGENT)}&shopHash=${encodeURIComponent(activeShop.id)}`;
    axios.get(agentConfigUrl).then(({ data }) => {
      if (!mounted) return;
      const configuredUrl = String(data || "").trim();
      const validUrl = /^https?:\/\//i.test(configuredUrl) ? configuredUrl : "";
      setPrinterAgentUrl(validUrl);
      if (!validUrl) setMode((current) => current === SILENT_DESKTOP ? NORMAL_PRINT : current);
    }).catch(() => {
      if (!mounted) return;
      setPrinterAgentUrl("");
      setMode((current) => current === SILENT_DESKTOP ? NORMAL_PRINT : current);
    });
    axios.get(licenseUrl).then(({ data }) => {
      if (mounted) setLicense(data?.jcJson?.license || null);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [activeShop.id, baseUrl, licenseUrl]);

  const patch = (key, value) => setConfigMap((current) => ({ ...current, [key]: value }));

  const setProfile = (channel, profileId) => {
    const profile = PROFILES.find((item) => item.value === profileId);
    setConfigMap((current) => ({
      ...current,
      [`${channel}_profile`]: profileId,
      [`${channel}_paper_width_mm`]: profile.width,
      [`${channel}_raster_width`]: profile.dots,
    }));
  };

  const validate = () => {
    if (mode !== SILENT_DESKTOP) return "";
    const agentPort = Number(configMap.silent_agent_port);
    if (!Number.isInteger(agentPort) || agentPort < 1 || agentPort > 65535) return "SNP Printer Agent port must be between 1 and 65535.";
    for (const { key, title } of visibleChannels) {
      if (!configMap[`${key}_enabled`]) continue;
      const type = configMap[`${key}_type`];
      if (type === "tcp" && (!String(configMap[`${key}_ip`] || "").trim() || !Number(configMap[`${key}_port`]))) return `${title}: enter a valid IP/host and port.`;
      if (type === "windows" && !String(configMap[`${key}_printer_name`] || "").trim()) return `${title}: enter the Windows printer name.`;
      if (type === "usb" && (!String(configMap[`${key}_vendor_id`] || "").trim() || !String(configMap[`${key}_product_id`] || "").trim())) return `${title}: enter USB vendor and product IDs.`;
      if (type === "ble" && (!String(configMap[`${key}_service_uuid`] || "").trim() || !String(configMap[`${key}_characteristic_uuid`] || "").trim())) return `${title}: enter BLE service and characteristic UUIDs.`;
    }
    return "";
  };

  const openAgentStore = async () => {
    setLicenseBusy(true);
    const storeWindow = window.open("", "_blank");
    try {
      let currentLicense = license;
      if (!currentLicense) {
        const getResponse = await axios.get(licenseUrl);
        currentLicense = getResponse.data?.jcJson?.license || null;
      }
      if (!currentLicense) {
        const requestResponse = await snpAxios.post(`${apiRoot}printer-agent-license`, {
          jcJson: { apiToken: getAuthSession().apiToken, shopHash: activeShop.id, websiteOrigin: window.location.origin },
        });
        if (requestResponse.data?.status !== SUCCESS) throw new Error(requestResponse.data?.errorMessage || "License request failed");
        currentLicense = requestResponse.data?.jcJson?.license;
      }
      if (!currentLicense?.licenseNumber) throw new Error("Printer Agent license is unavailable.");
      setLicense(currentLicense);
      if (storeWindow) {
        storeWindow.opener = null;
        storeWindow.location.href = printerAgentUrl;
      } else {
        window.location.href = printerAgentUrl;
      }
      showMessage("success", "License is ready. Microsoft Store opened.");
    } catch (error) {
      storeWindow?.close();
      showMessage("error", error?.response?.data?.errorMessage || error.message || "Unable to prepare Printer Agent download.");
    } finally {
      setLicenseBusy(false);
    }
  };

  const deleteLicense = async () => {
    if (!window.confirm("Delete this Printer Agent license? The installed agent will stop validating with it.")) return;
    setLicenseBusy(true);
    try {
      await axios.delete(licenseUrl);
      setLicense(null);
      showMessage("success", "Printer Agent license deleted.");
    } catch (error) {
      showMessage("error", error?.response?.data?.errorMessage || "Unable to delete the license.");
    } finally {
      setLicenseBusy(false);
    }
  };

  const copyLicense = async () => {
    await navigator.clipboard.writeText(license.licenseNumber);
    showMessage("success", "License number copied.");
  };

  const connectPrinter = async (channel) => {
    try {
      const text = await prepareSilentPrinter(configMap, channel);
      showMessage("success", text);
    } catch (error) {
      const text = error.message || "Printer could not be connected.";
      showMessage("error", text);
    }
  };

  const refreshWindowsPrinters = async (channel) => {
    setPrinterListBusy(true);
    try {
      const result = await listSilentWindowsPrinters(configMap);
      const printers = Array.isArray(result?.printers) ? result.printers : [];
      setWindowsPrinters(printers);
      if (!configMap[`${channel}_printer_name`] && result?.defaultPrinter) {
        patch(`${channel}_printer_name`, result.defaultPrinter);
      }
      showMessage("success", `${printers.length} Windows printer${printers.length === 1 ? "" : "s"} found${result?.defaultPrinter ? `; default is ${result.defaultPrinter}` : ""}.`);
    } catch (error) {
      const text = error.message || "Unable to load Windows printers from SNP Printer Agent.";
      showMessage("error", text);
    } finally {
      setPrinterListBusy(false);
    }
  };

  const maskedLicense = (value = "") => {
    const visible = Math.max(1, Math.ceil(value.length * 0.1));
    return `${"x".repeat(Math.max(0, value.length - visible))}${value.slice(-visible)}`;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      showMessage("error", validationError);
      return;
    }
    if (!record?.id) {
      showMessage("error", "Silent printing configuration is unavailable for this account.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        jcJson: {
          apiToken: getAuthSession().apiToken,
          shopHash: activeShop.id,
          id: record.id,
          configName: PRINTJARALLOWED,
          prefix: record.prefix || "",
          comments: record.comments || "",
          val: mode,
          map: persistedMap(configMap),
          type: record.type,
          allowed: record.allowed !== false,
        },
      };
      const response = await snpAxios.post(`${baseUrl}${PERSISTCONFIGS}`, payload);
      if (response.data?.status !== SUCCESS) throw new Error(response.data?.errorMessage || "Save failed");
      if (response.data?.jcJson) {
        setRecord(response.data.jcJson);
        setConfigMap({ ...defaultMap(), ...(response.data.jcJson.map || {}) });
      }
      showMessage("success", "Printer configuration saved.");
      onSaved?.(response.data?.jcJson || payload.jcJson);
    } catch (error) {
      showMessage("error", error?.response?.data?.errorMessage || error.message || "Printer configuration could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const renderTransportFields = (channel) => {
    const type = configMap[`${channel}_type`];
    if (type === "tcp") return <div className="printer-fields two"><Field label="Printer IP / host"><input value={configMap[`${channel}_ip`] || ""} onChange={(e) => patch(`${channel}_ip`, e.target.value)} placeholder="192.168.1.50" /></Field><Field label="Port"><input type="number" min="1" max="65535" value={configMap[`${channel}_port`] || ""} onChange={(e) => patch(`${channel}_port`, Number(e.target.value))} /></Field></div>;
    if (type === "windows") {
      const selected = configMap[`${channel}_printer_name`] || "";
      return <div className="printer-fields"><Field label="Windows printer queue" hint="Start the SNP Printer Agent, then refresh installed printers."><select value={selected} onChange={(e) => patch(`${channel}_printer_name`, e.target.value)}><option value="">Select installed printer…</option>{windowsPrinters.map((printer) => <option key={printer.name} value={printer.name}>{printer.name}{printer.default ? " (Default)" : ""}</option>)}{selected && !windowsPrinters.some((printer) => printer.name === selected) && <option value={selected}>{selected}</option>}</select></Field><button type="button" className="printer-connect" onClick={() => refreshWindowsPrinters(channel)} disabled={printerListBusy}>{printerListBusy ? "Loading printers…" : "Refresh Windows printers"}</button></div>;
    }
    if (type === "usb") return <div className="printer-fields two"><Field label="Vendor ID"><input value={configMap[`${channel}_vendor_id`] || ""} onChange={(e) => patch(`${channel}_vendor_id`, e.target.value)} placeholder="0x04b8" /></Field><Field label="Product ID"><input value={configMap[`${channel}_product_id`] || ""} onChange={(e) => patch(`${channel}_product_id`, e.target.value)} placeholder="0x0202" /></Field></div>;
    if (type === "ble") return <div className="printer-fields"><Field label="Service UUID"><input value={configMap[`${channel}_service_uuid`] || ""} onChange={(e) => patch(`${channel}_service_uuid`, e.target.value)} /></Field><Field label="Write characteristic UUID"><input value={configMap[`${channel}_characteristic_uuid`] || ""} onChange={(e) => patch(`${channel}_characteristic_uuid`, e.target.value)} /></Field></div>;
    return <div className="printer-fields two"><Field label="Baud rate"><input type="number" value={configMap[`${channel}_baud_rate`] || 9600} onChange={(e) => patch(`${channel}_baud_rate`, Number(e.target.value))} /></Field><Field label="Device / port hint" hint="Optional paired device or COM port name."><input value={configMap[`${channel}_device_name`] || ""} onChange={(e) => patch(`${channel}_device_name`, e.target.value)} placeholder="COM4" /></Field></div>;
  };

  if (loading) return <div className="printer-config-state">Loading printer configuration…</div>;

  return (
    <>
    <div className={`printer-config-page${embedded ? " embedded" : ""}`}>
      <div className="printer-config-header"><div><span className="printer-eyebrow">DASHBOARD SETTINGS</span><h1>Printer Configuration</h1><p>Choose normal browser printing or route each print channel to its own silent ESC/POS printer.</p></div></div>
      <div className="printer-mode-grid">
        <button type="button" className={`printer-mode ${mode === NORMAL_PRINT ? "selected" : ""}`} onClick={() => setMode(NORMAL_PRINT)}><strong>Use Normal Printing</strong><span>Generate a PDF and use the existing browser print flow.</span></button>
        {printerAgentUrl && <button type="button" className={`printer-mode ${mode === SILENT_DESKTOP ? "selected" : ""}`} onClick={() => setMode(SILENT_DESKTOP)}><strong>Use Silent Printing - Desktop</strong><span>Send ESC/POS output directly to printers configured on this desktop.</span></button>}
        <button type="button" className={`printer-mode ${mode === SILENT_MOBILE_TABLET ? "selected" : ""}`} onClick={() => setMode(SILENT_MOBILE_TABLET)}><strong>Use Silent Printing - Mobile/Tablet</strong><span>Generate ESC/POS text for the SNP Printer mobile application.</span></button>
      </div>
      {mode === SILENT_MOBILE_TABLET && <section className="printer-mobile-app-card">
        <div><span className="printer-eyebrow">MOBILE / TABLET APP</span><h2>Install SNP Printer App</h2><p>Contact A2Z admin for Mobile app.</p></div>
        <div className="printer-store-links">
          {mobileAppLinks?.androidUrl && <a className="printer-store-link google-play" href={mobileAppLinks.androidUrl} target="_blank" rel="noreferrer"><GooglePlayIcon /><span><small>GET IT ON</small><strong>Google Play</strong></span></a>}
          {mobileAppLinks?.iosUrl && <a className="printer-store-link app-store" href={mobileAppLinks.iosUrl} target="_blank" rel="noreferrer"><AppleStoreIcon /><span><small>Download on the</small><strong>App Store</strong></span></a>}
        </div>
      </section>}
      {mode === SILENT_DESKTOP && <section className="printer-agent-card">
        <div><span className="printer-eyebrow">INSTALL FIRST</span><h2>SNP Printer Agent</h2><p>Download, install, activate and start the Agent before connecting TCP or Windows printers.</p><div className="printer-agent-port"><Field label="Local Agent port" hint="Do not change this port unless you first change the same value in SNP Printer Agent → System Config."><input className="printer-agent-port-input" type="number" min="1" max="65535" value={configMap.silent_agent_port || 17312} onChange={(e) => patch("silent_agent_port", Number(e.target.value))} /></Field></div></div>
        <div className="printer-license-panel">
          {license?.licenseNumber ? <><code title="License number is masked">{maskedLicense(license.licenseNumber)}</code><button type="button" onClick={copyLicense} disabled={licenseBusy}>Copy</button><button type="button" className="danger" onClick={deleteLicense} disabled={licenseBusy}>Delete</button></> : <span>No license created yet.</span>}
        </div>
        <button type="button" className="printer-store-link microsoft-store" onClick={openAgentStore} disabled={licenseBusy}><MicrosoftStoreIcon /><span><small>GET IT FROM</small><strong>{licenseBusy ? "Preparing…" : "Microsoft Store"}</strong></span></button>
      </section>}
      {mode === SILENT_DESKTOP && <div className="printer-channel-grid">
        {visibleChannels.map(({ key, title, description }) => <section className={`printer-card ${!configMap[`${key}_enabled`] ? "disabled" : ""}`} key={key}>
          <div className="printer-card-title"><div><h2>{title}</h2><p>{description}</p></div><label className="printer-toggle"><input type="checkbox" checked={Boolean(configMap[`${key}_enabled`])} onChange={(e) => patch(`${key}_enabled`, e.target.checked)} /><span>{configMap[`${key}_enabled`] ? "Enabled" : "Disabled"}</span></label></div>
          {configMap[`${key}_enabled`] && <>
            <div className="printer-fields two"><Field label="Connection type"><select value={configMap[`${key}_type`]} onChange={(e) => patch(`${key}_type`, e.target.value)}>{TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></Field><Field label="ESC/POS printer profile"><select value={configMap[`${key}_profile`]} onChange={(e) => setProfile(key, e.target.value)}>{PROFILES.map((profile) => <option key={profile.value} value={profile.value}>{profile.label}</option>)}</select></Field></div>
            {renderTransportFields(key)}
            <div className="printer-fields two"><Field label="Paper cut"><select value={configMap[`${key}_cut_mode`] || "partial"} onChange={(e) => patch(`${key}_cut_mode`, e.target.value)}><option value="none">No cut</option><option value="full">Full cut</option><option value="partial">Partial cut</option></select></Field><Field label="Copies"><input type="number" min="1" max="10" value={configMap[`${key}_copies`] || 1} onChange={(e) => patch(`${key}_copies`, Number(e.target.value))} /></Field></div>
            <button type="button" className="printer-connect" onClick={() => connectPrinter(key)}>Connect / Pair printer</button>
          </>}
        </section>)}
      </div>}
      <div className="printer-actions"><SnpButton variant="contained" color="primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Printer Configuration"}</SnpButton></div>
    </div>
    <Snackbar
      open={toast.open}
      autoHideDuration={3500}
      message={toast.text}
      onClose={(_, reason) => reason !== "clickaway" && setToast((current) => ({ ...current, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      ContentProps={{
        sx: {
          backgroundColor: toast.type === "success" ? "#2e7d32" : "#c62828",
          color: "#fff",
        },
      }}
    />
    </>
  );
}
